import { useState } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { Loader2, Mail, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | sent
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setPhase('sent');
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/user-not-found') {
        setError('لا يوجد حساب مرتبط بهذا البريد الإلكتروني');
      } else if (err.code === 'auth/invalid-email') {
        setError('البريد الإلكتروني غير صالح');
      } else {
        setError('حدث خطأ، يرجى المحاولة مرة أخرى');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#050505] relative overflow-hidden px-4 transition-colors duration-700">
      {/* Ambient Cinematic Glows */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-500/20 dark:bg-primary-600/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-500/10 dark:bg-purple-600/10 rounded-full blur-[150px] translate-y-1/3 -translate-x-1/4 pointer-events-none"></div>

      <div className="relative z-10 max-w-md w-full bg-white/80 dark:bg-zinc-900/60 backdrop-blur-3xl rounded-lg shadow-2xl shadow-slate-200/50 dark:shadow-black/80 border border-slate-100 dark:border-white/10 p-8 md:p-10 transform transition-all duration-500">

        {phase === 'idle' ? (
          <>
            <div className="text-center mb-10">
              <Link to="/" className="inline-block text-4xl font-black bg-clip-text text-transparent bg-gradient-to-l from-primary-600 to-primary-400 dark:from-primary-400 dark:to-purple-400 mb-3 drop-shadow-sm">
                لُبـــــــــاب
              </Link>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">استعادة كلمة المرور</h2>
              <p className="text-slate-500 dark:text-zinc-400 mt-2 font-bold">أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين</p>
            </div>

            {error && (
              <div className="mb-6 bg-rose-50/80 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 text-rose-600 dark:text-rose-400 p-4 rounded-2xl flex items-start gap-3 backdrop-blur-sm animate-in fade-in zoom-in-95 duration-300">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-md font-bold leading-relaxed">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" dir="rtl">
              <div>
                <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">البريد الإلكتروني</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none transition-colors group-focus-within:text-primary-500">
                    <Mail className="h-5 w-5 text-slate-400 dark:text-zinc-500 transition-colors group-focus-within:text-primary-500" />
                  </div>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block text-lg w-full pr-12 pl-4 py-3.5 border border-slate-200 dark:border-white/10 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white/50 dark:bg-black/20 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 transition-all duration-300 hover:bg-white dark:hover:bg-white/5 font-medium"
                    placeholder="أدخل بريدك الإلكتروني"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex text-xl font-semibold justify-center items-center py-4 px-4 border border-transparent rounded-2xl text-lg font-bold text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 focus:outline-none transition-all duration-300 hover:shadow-[0_0_25px_rgba(245,158,11,0.4)] hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : 'إرسال رابط الاستعادة'}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-slate-200/50 dark:border-white/5 text-center">
              <Link to="/login" className="inline-flex items-center gap-2 text-lg font-semibold text-slate-500 dark:text-zinc-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors group">
                <ArrowRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                العودة لتسجيل الدخول
              </Link>
            </div>
          </>
        ) : (
          /* ── PHASE: SENT ── */
          <div className="text-center py-6 space-y-6">
            {/* Animated Mail Icon */}
            <div className="relative w-24 h-24 mx-auto">
              <div className="absolute inset-0 bg-primary-500/20 rounded-full animate-ping opacity-30"></div>
              <div className="relative w-24 h-24 bg-primary-50 dark:bg-primary-500/10 rounded-full flex items-center justify-center border-2 border-primary-200 dark:border-primary-500/30 shadow-xl shadow-primary-500/20">
                <Mail className="w-10 h-10 text-primary-600 dark:text-primary-400" />
              </div>
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-3">تحقّق من بريدك الإلكتروني</h2>
              <p className="text-slate-500 dark:text-zinc-400 font-bold text-lg leading-relaxed max-w-sm mx-auto">
                تم إرسال رابط إعادة تعيين كلمة المرور إلى<br />
                <span className="text-primary-600 dark:text-primary-400 font-black">{email}</span>
              </p>
            </div>

            <div className="flex items-center gap-2 justify-center text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-2xl py-3 px-5">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <span className="font-bold text-md">تحقق من صندوق الوارد والبريد المزعج</span>
            </div>

            <div className="pt-4">
              <Link to="/login" className="inline-flex items-center gap-2 text-lg font-bold text-primary-600 dark:text-primary-400 hover:text-primary-500 transition-colors group">
                <ArrowRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                العودة لتسجيل الدخول
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
